import React, { useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DotsSixVertical, Plus, Trash } from '@phosphor-icons/react';

const fieldInputProps = { notched: false };
const fieldTextFieldSx = {
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    mb: 0.75,
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'text.secondary',
    '&.Mui-focused': {
      color: 'text.secondary'
    }
  },
  '& .MuiOutlinedInput-root': {
    mt: 0
  }
};

function slugifyFieldName(label) {
  const base = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return base || 'field';
}

function ensureUniqueName(name, fields, excludeIndex) {
  const used = new Set(
    fields
      .map((f, i) => (i === excludeIndex ? null : f.name))
      .filter(Boolean)
  );
  if (!used.has(name)) return name;
  let suffix = 2;
  let candidate = `${name}_${suffix}`;
  while (used.has(candidate)) {
    suffix += 1;
    candidate = `${name}_${suffix}`;
  }
  return candidate;
}

function createFieldClientId() {
  return `ai_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function AgreementAdditionalInformationPanel({ fields = [], onChange }) {
  useEffect(() => {
    if (!fields.some((f) => !f.clientId)) return;
    onChange(
      fields.map((f) => (f.clientId ? f : { ...f, clientId: createFieldClientId() }))
    );
  }, [fields, onChange]);

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination || result.source.index === result.destination.index) return;
      const next = [...fields];
      const [removed] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, removed);
      onChange(next.map((f, i) => ({ ...f, order: i })));
    },
    [fields, onChange]
  );

  const updateField = (index, patch) => {
    const next = fields.map((f, i) => {
      if (i !== index) return f;
      const updated = { ...f, ...patch };
      return updated;
    });
    onChange(next);
  };

  const addField = () => {
    const label = `Field ${fields.length + 1}`;
    const name = ensureUniqueName(slugifyFieldName(label), fields, -1);
    onChange([
      ...fields,
      {
        clientId: createFieldClientId(),
        type: 'text',
        name,
        label,
        placeholder: '',
        order: fields.length
      }
    ]);
  };

  const removeField = (index) => {
    onChange(fields.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i })));
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', flex: 1, overflow: 'auto', minHeight: 0 }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
        Additional information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, width: '100%' }}>
        Add fields for addresses and other details couples must provide before signing. Responses are saved to their couple record. Couples must complete these fields before they can sign.
      </Typography>

      {fields.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No additional fields yet. Add text or long-text fields for addresses, delivery details, and similar information.
          </Typography>
          <Button variant="outlined" startIcon={<Plus size={18} />} onClick={addField}>
            Add field
          </Button>
        </Paper>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="agreement-additional-fields">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  {fields.map((field, index) => {
                    const dragId = field.clientId || `ai_pending_${index}`;
                    return (
                    <Draggable key={dragId} draggableId={dragId} index={index}>
                      {(dragProvided) => (
                        <Paper
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box
                              {...dragProvided.dragHandleProps}
                              sx={{ pt: 1, cursor: 'grab', color: 'text.secondary', flexShrink: 0 }}
                              aria-label="Drag to reorder"
                            >
                              <DotsSixVertical size={20} />
                            </Box>
                            <Grid container spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Field name"
                                  value={field.label}
                                  onChange={(e) => updateField(index, { label: e.target.value })}
                                  onBlur={() => {
                                    const slug = slugifyFieldName(field.label);
                                    const nextName = ensureUniqueName(slug, fields, index);
                                    if (nextName !== field.name) {
                                      updateField(index, { name: nextName });
                                    }
                                  }}
                                  required
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={fieldInputProps}
                                  sx={fieldTextFieldSx}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Field type"
                                  value={field.type || 'text'}
                                  onChange={(e) => updateField(index, { type: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={fieldInputProps}
                                  sx={fieldTextFieldSx}
                                >
                                  <MenuItem value="text">Short text</MenuItem>
                                  <MenuItem value="textarea">Long text</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Placeholder (optional)"
                                  value={field.placeholder || ''}
                                  onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={fieldInputProps}
                                  sx={fieldTextFieldSx}
                                />
                              </Grid>
                            </Grid>
                            <IconButton
                              aria-label="Remove field"
                              onClick={() => removeField(index)}
                              sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.25 }}
                            >
                              <Trash size={18} />
                            </IconButton>
                          </Box>
                        </Paper>
                      )}
                    </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
          <Button variant="outlined" startIcon={<Plus size={18} />} onClick={addField}>
            Add field
          </Button>
        </>
      )}
    </Box>
  );
}

export default AgreementAdditionalInformationPanel;
